import { Button } from "@/components/primitives/button";
import { Input } from "@/components/primitives/input";
import { FormStepProps } from "@/components/shared/Wizard/Wizard";
import { MAX_PASSWORD_LENGTH } from "@/config/constants";
import { useToast } from "@/hooks/useToast";
import { copyTextToClipboard } from "@/lib/clipboard";
import { isUserAuthenticated, loginUser } from "@/middleware/auth";
import { LoginFormType } from "@/schemas/auth";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type ShowPasswordData = {
  password: string;
};
export default function ShowPasswordStep({
  onClose,
  formData,
}: FormStepProps<ShowPasswordData>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const userFingerPrint = isUserAuthenticated();

  const loginData = {
    ...formData?.userData,
    ...formData?.password,
  } as LoginFormType;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  async function handleCopy() {
    const copied = await copyTextToClipboard(loginData.password);
    if (copied) {
      toast({
        description: t("forgot.copySuccess"),
        variant: "default",
      });
    }
  }

  async function handleLogin() {
    try {
      await loginUser(loginData);
      navigate("/", { replace: true });
    } catch (error) {
      onClose?.();
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }
  const showLogin = !userFingerPrint?.username;
  return (
    <div className="flex size-full flex-col items-stretch justify-center gap-4">
      <h1 className="text-lg">{t("forgot.successTitle")}</h1>
      <p className="opacity-50">{t("forgot.successDescription")}</p>

      <div className="relative w-full">
        <Input
          className="rounded-lg border-border bg-card py-6 pr-9 placeholder:opacity-70"
          type={showPassword ? "text" : "password"}
          placeholder={t("recover.password")}
          autoComplete="password"
          autoFocus
          maxLength={MAX_PASSWORD_LENGTH}
          value={loginData.password}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
          onClick={togglePasswordVisibility}
        >
          {showPassword ? (
            <EyeIcon className="size-5 text-foreground/50" />
          ) : (
            <EyeOffIcon className="size-5 text-foreground/50" />
          )}
        </button>
      </div>

      <p className="opacity-50">{t("forgot.successTip")}</p>

      <div className="flex w-full items-center justify-end gap-4">
        <Button type="button" onClick={handleCopy} variant="outline">
          {t("copy")}
        </Button>
        {showLogin && <Button onClick={handleLogin}>{t("login.title")}</Button>}
        {!showLogin && (
          <Button onClick={() => onClose?.()}>{t("close")}</Button>
        )}
      </div>
    </div>
  );
}
